import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import AddFundsModal from './AddFundsModal';

const formatE164Display = (e164) => {
  if (!e164) return '';
  if (e164.startsWith('+91') && e164.length === 13) {
    return `+91 ${e164.slice(3, 8)} ${e164.slice(8)}`;
  }
  return e164;
};

const formatCurrency = (amount, currency) => {
  if (currency === 'INR') return `\u20B9${amount}`;
  if (currency === 'USD') return `$${amount}`;
  return `${amount} ${currency}`;
};

const formatBalance = (amount) => {
  if (amount == null) return '0.00';
  return Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const BuyNumberModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1 = browse, 2 = confirm
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [phoneRentalRate, setPhoneRentalRate] = useState(null);
  const [gstOnFile, setGstOnFile] = useState(true);
  const [showAddFunds, setShowAddFunds] = useState(false);

  const perPage = 15;

  const fetchInventory = useCallback(async (searchTerm, pageNum) => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/api/v1/phone-numbers/inventory', {
        params: {
          country: 'IN',
          search: searchTerm || undefined,
          page: pageNum,
          perPage,
        },
      });
      const data = res.data?.data;
      setInventory(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
      setError('Failed to load available numbers.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWalletBalance = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/wallet/balance');
      const data = res.data?.data;
      setWalletBalance(data?.balance ?? 0);
      setPhoneRentalRate(data?.phoneRentalRate ?? 1000);
      setGstOnFile(data?.gstOnFile ?? false);
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err);
      setWalletBalance(0);
      setPhoneRentalRate(1000);
    }
  }, []);

  useEffect(() => {
    fetchInventory(search, page);
  }, [page]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchInventory(search, 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, fetchInventory]);

  // Fetch wallet balance and phone rental rate on mount, refresh on confirm step
  useEffect(() => {
    fetchWalletBalance();
  }, [fetchWalletBalance]);

  useEffect(() => {
    if (step === 2) {
      fetchWalletBalance();
    }
  }, [step, fetchWalletBalance]);

  const totalCost = selectedNumber && phoneRentalRate ? phoneRentalRate : 0;
  const hasSufficientBalance = walletBalance !== null && walletBalance >= totalCost;
  const topUpShortfall = walletBalance !== null ? Math.max(0, Math.ceil(totalCost - walletBalance)) : 0;

  const handlePurchase = async () => {
    if (!selectedNumber || !hasSufficientBalance) return;
    setPurchasing(true);
    setError('');

    try {
      await api.post('/api/v1/phone-numbers/purchase-wallet', {
        e164: selectedNumber.e164,
        country: selectedNumber.country || 'IN',
        region: selectedNumber.region || null,
        monthlyFee: phoneRentalRate,
        setupFee: 0,
        currency: selectedNumber.currency || 'INR',
      });
      setSuccess(true);
    } catch (err) {
      console.error('Purchase failed:', err);
      const msg = err?.response?.data?.message || 'Failed to purchase number. Please try again.';
      setError(msg);
    } finally {
      setPurchasing(false);
    }
  };

  const handleAddFundsClose = () => {
    setShowAddFunds(false);
  };

  const handleAddFundsSuccess = () => {
    setShowAddFunds(false);
    fetchWalletBalance();
  };

  const totalPages = Math.ceil(total / perPage);

  // Success screen
  if (success) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onSuccess} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} className="text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Number Purchased!</h3>
            <p className="text-sm text-gray-500 mb-1">
              You have successfully purchased
            </p>
            <p className="text-lg font-bold text-gray-900 mb-6">
              {formatE164Display(selectedNumber?.e164)}
            </p>
            <button
              onClick={onSuccess}
              className="px-6 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {step === 1 ? 'Buy Phone Number' : 'Confirm Purchase'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {step === 1 ? 'Browse available Indian phone numbers.' : 'Review and confirm your purchase.'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mx-6 mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
              <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
              {error}
              <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
                <Icon name="X" size={12} />
              </button>
            </div>
          )}

          {/* Body */}
          {step === 1 ? (
            <>
              {/* Search */}
              <div className="px-6 pt-4 pb-3">
                <div className="relative">
                  <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by starting digits (e.g. 8065)..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Number List */}
              <div className="flex-1 overflow-y-auto px-6 pb-4">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-7 h-7 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                  </div>
                ) : inventory.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Icon name="SearchX" size={28} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No numbers found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {inventory.map((num) => (
                      <button
                        key={num.id || num.e164}
                        type="button"
                        onClick={() => setSelectedNumber(num)}
                        className={`w-full text-left flex items-center gap-3 p-3.5 rounded-xl border transition-all ${
                          selectedNumber?.e164 === num.e164
                            ? 'border-[#2F55FF] bg-blue-50/60 shadow-sm'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          selectedNumber?.e164 === num.e164 ? 'bg-[#2F55FF]/10' : 'bg-gray-100'
                        }`}>
                          <Icon name="Phone" size={16} className={selectedNumber?.e164 === num.e164 ? 'text-[#2F55FF]' : 'text-gray-500'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{formatE164Display(num.e164)}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {num.region && <span className="text-xs text-gray-500">{num.region}</span>}
                            <span className="text-xs text-gray-400">&middot;</span>
                            <span className="text-xs text-gray-500">Voice</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-gray-900">₹{phoneRentalRate?.toLocaleString('en-IN')}/mo</p>
                        </div>
                        {selectedNumber?.e164 === num.e164 && (
                          <Icon name="CheckCircle" size={16} className="text-[#2F55FF] flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>Showing {inventory.length} of {total} numbers</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-gray-600 font-medium">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white">
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!selectedNumber}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                    selectedNumber
                      ? 'bg-[#2F55FF] text-white hover:bg-blue-700 shadow-sm'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                  <Icon name="ArrowRight" size={15} />
                </button>
              </div>
            </>
          ) : (
            /* Step 2: Confirm Purchase */
            <>
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {/* Number Card */}
                <div className="border border-gray-200 rounded-xl p-5 mb-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Icon name="Phone" size={22} className="text-[#2F55FF]" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">{formatE164Display(selectedNumber?.e164)}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">{selectedNumber?.country}</span>
                        {selectedNumber?.region && (
                          <>
                            <span className="text-xs text-gray-400">&middot;</span>
                            <span className="text-xs text-gray-500">{selectedNumber?.region}</span>
                          </>
                        )}
                        <span className="text-xs text-gray-400">&middot;</span>
                        <span className="text-xs text-green-600 font-medium">Voice Enabled</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cost Summary */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Cost Summary</h4>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Monthly Rental</span>
                      <span className="text-sm font-medium text-gray-900">₹{phoneRentalRate?.toLocaleString('en-IN')}/month</span>
                    </div>
                    <div className="border-t border-gray-200 pt-2.5 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">Due Now</span>
                      <span className="text-base font-bold text-gray-900">₹{phoneRentalRate?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">
                    Deducted from your wallet now and on each monthly renewal. GST is charged only when you add funds.
                  </p>
                </div>

                {/* Wallet Balance */}
                <div className={`mt-4 flex items-center justify-between px-4 py-3 rounded-xl border ${
                  walletBalance === null
                    ? 'bg-gray-50 border-gray-200'
                    : hasSufficientBalance
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <Icon name="Wallet" size={16} className={
                      walletBalance === null ? 'text-gray-400' : hasSufficientBalance ? 'text-green-600' : 'text-red-600'
                    } />
                    <span className="text-sm text-gray-700">Wallet Balance</span>
                  </div>
                  <span className={`text-sm font-semibold ${
                    walletBalance === null ? 'text-gray-400' : hasSufficientBalance ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {walletBalance === null ? '...' : `₹${formatBalance(walletBalance)}`}
                  </span>
                </div>

                {/* Insufficient balance warning */}
                {walletBalance !== null && !hasSufficientBalance && (
                  <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                    <Icon name="AlertTriangle" size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-700">
                        Insufficient balance. You need ₹{formatBalance(totalCost - walletBalance)} more to purchase this number.
                      </p>
                      <button
                        onClick={() => setShowAddFunds(true)}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2F55FF] text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Icon name="Plus" size={12} />
                        Add Funds
                      </button>
                    </div>
                  </div>
                )}

                {/* Info */}
                {hasSufficientBalance && (
                  <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                    <Icon name="Info" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700">
                      The amount will be deducted from your wallet balance. The number will be activated instantly. You can release it anytime from Settings.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white">
                <button
                  onClick={() => { setStep(1); setError(''); }}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors flex items-center gap-2"
                >
                  <Icon name="ArrowLeft" size={15} />
                  Back
                </button>
                <button
                  onClick={handlePurchase}
                  disabled={purchasing || !hasSufficientBalance}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl transition-colors shadow-sm ${
                    hasSufficientBalance && !purchasing
                      ? 'bg-[#2F55FF] text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {purchasing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Purchasing...
                    </>
                  ) : (
                    <>
                      <Icon name="Wallet" size={15} />
                      Pay from Wallet
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add Funds Modal (rendered on top) */}
      {showAddFunds && (
        <AddFundsModal
          defaultAmount={topUpShortfall}
          gstOnFile={gstOnFile}
          onClose={handleAddFundsClose}
          onSuccess={handleAddFundsSuccess}
        />
      )}
    </>
  );
};

export default BuyNumberModal;
